import BaseLayout from './BaseLayout';
import { LayoutProps } from './types';
import clsx from 'clsx';
import Header from './components/Header';
import Sidebar from './components/Sidebar';
import { useStore } from '@/hooks/useStore';
import IconButton from '../ui/IconButton';
import { Link } from 'react-router-dom';
import { IconArrowLeft } from '../icons';
import { useRef, useState, useEffect, useCallback } from 'react';
import {
  BreadcrumbNav,
  Heading
} from 'stubben-edge-unified-frontend-react-components';
import {
  readSidebarExpanded,
  writeSidebarExpanded
} from './components/Sidebar/sidebarExpandedStorage';

const MainLayout = ({
  title,
  heading,
  className,
  backLink,
  children,
  hideTitle,
  hideSidebar = false,
  collapseSidebar = false,
  fillViewport = false,
  description,
  padding = true,
  background = 'dim',
  hideHeader = false,
  breadcrumbItems,
  breadcrumbClass = '16px 24px',
  breadcrumbBackground = 'dim',
  tabTitle
}: LayoutProps) => {
  const { interfaceStore } = useStore();
  const headerRef = useRef<HTMLDivElement>(null);
  const [headerHeight, setHeaderHeight] = useState(81);
  const [expanded, setExpanded] = useState(() =>
    collapseSidebar ? false : readSidebarExpanded()
  );
  const collapsed = !expanded;

  useEffect(() => {
    const updateHeaderHeight = () => {
      if (headerRef.current) {
        setHeaderHeight(headerRef.current.offsetHeight);
      }
    };

    updateHeaderHeight();

    const resizeObserver = new ResizeObserver(updateHeaderHeight);
    if (headerRef.current) {
      resizeObserver.observe(headerRef.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  useEffect(() => {
    if (collapseSidebar) {
      setExpanded(false);
    } else {
      setExpanded(readSidebarExpanded());
    }
  }, [collapseSidebar]);

  const toggleExpanded = useCallback(() => {
    setExpanded((current) => {
      const next = !current;
      writeSidebarExpanded(next);
      return next;
    });
  }, []);

  return (
    <BaseLayout title={title}>
      <div ref={headerRef}>
        <Header />
      </div>
      <div
        className="relative flex overflow-hidden"
        style={
          fillViewport
            ? { height: `calc(100vh - ${headerHeight}px)` }
            : { maxHeight: `calc(100vh - ${headerHeight}px)` }
        }
      >
        {!hideSidebar && (
          <Sidebar
            collapsed={collapsed}
            onToggleCollapsed={toggleExpanded}
          />
        )}
        <div
          id="main-content"
          className={clsx(
            'relative flex min-h-0 min-w-0 flex-1 flex-col',
            fillViewport ? 'overflow-hidden' : 'overflow-y-auto',
            background === 'dim'
              ? 'bg-gray-50'
              : background === 'white'
              ? 'bg-white'
              : background
          )}
        >
          <main
            className={clsx(
              className,
              padding && 'p-6 xl:px-9',
              fillViewport && 'flex min-h-0 flex-1 flex-col overflow-hidden'
            )}
          >
            {breadcrumbItems && breadcrumbItems.length > 0 && (
              <div
                className={clsx(
                  breadcrumbBackground === 'dim'
                    ? 'bg-gray-50'
                    : breadcrumbBackground === 'white'
                    ? 'bg-white'
                    : background === 'dim'
                    ? 'bg-gray-50'
                    : background === 'white'
                    ? 'bg-white'
                    : '',
                  hideHeader ? '' : 'mb-6',
                  fillViewport && 'shrink-0'
                )}
              >
                <BreadcrumbNav
                  items={breadcrumbItems}
                  padding={breadcrumbClass}
                  domainTitle=" | Stubbenedge"
                  tabTitle={tabTitle}
                />
              </div>
            )}
            {!hideHeader && (
              <div className="mb-6 flex flex-col gap-4">
                <div className="flex items-center">
                  {(backLink || interfaceStore.onBackClick) && (
                    <IconButton
                      variant="text"
                      as={backLink ? Link : 'button'}
                      to={backLink || undefined}
                      size="large"
                      rounded="full"
                      className="-ml-2 mr-2 shrink-0"
                      onClick={() =>
                        interfaceStore.onBackClick
                          ? interfaceStore.onBackClick()
                          : undefined
                      }
                    >
                      <IconArrowLeft />
                    </IconButton>
                  )}
                  {heading && heading}
                  {title && !heading && (
                    <Heading size="sm">{!hideTitle && title}</Heading>
                  )}
                </div>
                {description && description}
              </div>
            )}
            {children}
          </main>
        </div>
      </div>
    </BaseLayout>
  );
};

export default MainLayout;
